// React imports
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// React Router imports
import { Link } from 'react-router-dom';

// AWS Amplify imports
import { fetchUserAttributes, signIn, signOut, signUp } from 'aws-amplify/auth';

// Redux imports
import { AppDispatch, RootState } from '../redux/store';
import { useDispatch } from 'react-redux';
import { clearUser, setUser } from '../redux/userSlice';
import { clearUserTypes, setUserTypes } from '../redux/userTypesSlice';
import { clearDepartments, setDepartments } from '../redux/departmentsSlice';
import { clearSchools, setSchools } from '../redux/schoolsSlice';
import { clearSupports, setSupports } from '../redux/supportSlice';
import {
  clearAllIPStatuses,
  setIPStatusesList,
} from '../redux/ipStatusesSlice';

// Utility imports
import { validatePassword } from '../utils/CommonUtils';
import { toast } from 'react-toastify';

// Constant imports
import { APPROVED, PENDING, REJECTED, TENANT_ID } from '../constants/Constants';

// Asset imports (images and icons)
import NoPhoto from '../assets/no-photo.png';

// Style imports
import '../styles/ClaimProfileFlow.css';

// Component imports
import Button from './common/Button';
import Loading from './common/Loading';
import Label from './common/Label';
import PasswordInput from './common/PasswordInput';
import Text from './common/Text';

// API imports
import {
  getDepartments,
  getIPStatus,
  getSchools,
  getSupport,
  getUserTypes,
  UpdateUserStatus,
  VerifyToken,
} from '../api/UserService';

// Types imports
import {
  DepartmentListResponse,
  InternalServerError,
  SchoolListResponse,
  PartnershipListResponse,
  User,
  UserRolesResponse,
  VerifyTokenResponse,
  IPStatusesListResponse,
} from '../types/types';
import { useSelector } from 'react-redux';

const ClaimProfileFlow: React.FC = () => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const token = queryParams.get('token');
  const [loading, setLoading] = useState<boolean>(true);
  const [buttonLoading, setButtonLoading] = useState<boolean | null>(null);
  const [step, setStep] = useState<
    | 'accept-invite'
    | 'decline-appeal'
    | 'declined'
    | 'set-password'
    | 'review'
    | 'complete'
    | 'admin-not-approved'
    | 'account-registered-awaiting-login'
  >('accept-invite');
  const navigate = useNavigate();
  const [user, setClaimUser] = useState<User>({
    tenant_id: 0,
    id: 0,
    admin_status_id: 0,
    user_status_id: 0,
    first_name: '',
    last_name: '',
    email: '',
    profile_image_url: '',
    cognito_user_id: '',
    user_type_id: 0,
    school_id: '',
    department_id: '',
  });
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const reduxUser = useSelector((state: RootState) => state.user.userDetails);

  // Create a ref to hold the current user state
  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user; // Update the ref when user state changes
  }, [user]);

  const getUserTypesFromAPI = async () => {
    const result = await getUserTypes();
    if ('code' in result) {
      console.error('Error fetching user types:', result);
    } else {
      if ((result as UserRolesResponse).user_types.length > 0) {
        dispatch(setUserTypes((result as UserRolesResponse).user_types));
      } else {
        console.log('user types object is empty');
      }
    }
  };

  const getSchoolsFromAPI = async () => {
    const result = await getSchools(TENANT_ID, true);
    if ('code' in result) {
      console.error('Error fetching schools:', result);
    } else {
      if ((result as SchoolListResponse).schools.length > 0) {
        dispatch(setSchools((result as SchoolListResponse).schools));
      } else {
        console.log('schools object is empty');
      }
    }
  };

  const getDepartmentsFromAPI = async () => {
    const result = await getDepartments(TENANT_ID, true);
    if ('code' in result) {
      console.error('Error fetching departments:', result);
    } else {
      if ((result as DepartmentListResponse).departments.length > 0) {
        dispatch(
          setDepartments((result as DepartmentListResponse).departments),
        );
      } else {
        console.log('departments object is empty');
      }
    }
  };

  const getSupportsFromAPI = async () => {
    const result = await getSupport();
    if ('code' in result) {
      console.error('Error fetching Supports:', result);
    } else {
      if (
        result &&
        (result as PartnershipListResponse).support_or_partnership &&
        (result as PartnershipListResponse).support_or_partnership.length > 0
      ) {
        dispatch(
          setSupports(
            (result as PartnershipListResponse).support_or_partnership,
          ),
        );
      } else {
        console.log('Supports object is empty');
      }
    }
  };

  const getIPStatusesFromAPI = async () => {
    const result = await getIPStatus();
    if ('code' in result) {
      console.error('Error fetching ip statuses:', result);
    } else {
      if (
        result &&
        (result as IPStatusesListResponse).ip_statuses &&
        (result as IPStatusesListResponse).ip_statuses.length > 0
      ) {
        dispatch(
          setIPStatusesList((result as IPStatusesListResponse).ip_statuses),
        );
      } else {
        console.log('ip statuses object is empty');
      }
    }
  };

  useEffect(() => {
    if (token) {
      const fetchVerifyToken = async () => {
        try {
          if (reduxUser)
            //Signout the logged in user
            await handleLogout();

          const result = await VerifyToken(token);
          if ('code' in result) {
            // Handle the InternalServerError
            console.error('Error verifying the token:', result);
            if (result.code === 'NOT_FOUND') {
              setIsTokenValid(false);
              setLoading(false);
            } else {
              setIsTokenValid(false);
              setLoading(false);
            }
          } else {
            setIsTokenValid(true);
            setLoading(false);
            const resUser = result as VerifyTokenResponse;
            const claimUserData = {
              id: resUser.id,
              admin_status_id: resUser.admin_status_id,
              cognito_user_id: resUser.cognito_user_id,
              email: resUser.email,
              first_name: resUser.first_name,
              last_name: resUser.last_name,
              profile_image_url: resUser.profile_image_url,
              tenant_id: resUser.tenant_id,
              user_status_id: resUser.user_status_id,
              user_type_id: resUser.user_type_id,
              school_id: resUser.school_name,
              department_id: resUser.department_name,
            };
            if (
              resUser.admin_status_id === REJECTED ||
              resUser.user_status_id === REJECTED
            ) {
              setStep('declined');
            } else if (resUser.admin_status_id === PENDING) {
              setStep('admin-not-approved');
            } else if (
              resUser.admin_status_id === APPROVED &&
              resUser.user_status_id === APPROVED &&
              resUser.cognito_user_id === null
            ) {
              setStep('set-password');
              setClaimUser(claimUserData);
            } else if (
              resUser.admin_status_id === APPROVED &&
              resUser.user_status_id === APPROVED &&
              resUser.cognito_user_id !== null
            ) {
              setStep('account-registered-awaiting-login');
            } else if (
              resUser.admin_status_id === APPROVED &&
              resUser.user_status_id === PENDING
            ) {
              setClaimUser(claimUserData);
            }
          }
        } catch (error) {
          console.error('Error during token verification:', error);
          setIsTokenValid(null);
          setLoading(false);
        }
      };
      fetchVerifyToken();
    } else {
      setIsTokenValid(null);
      setLoading(false);
    }
  }, [token]);

  const handleLogout = async () => {
    try {
      await signOut();
      dispatch(clearUser());
      dispatch(clearUserTypes());
      dispatch(clearSupports());
      dispatch(clearDepartments());
      dispatch(clearSchools());
      dispatch(clearAllIPStatuses());
    } catch (error) {
      console.error('ClainProfileflow: Error during logout process:', error);
    }
  };

  const handleAcceptInvitation = async () => {
    setButtonLoading(true);
    const result = await UpdateUserStatus(user.id, { status: 'accept' });
    if ('code' in result) {
      // Handle the InternalServerError
      setError(result.message);
      setButtonLoading(false);
    } else {
      // Handle the successful response
      if (
        (result as InternalServerError).message ===
        'Successfully updated the user_status_id.'
      ) {
        setStep('set-password');
      }
      setButtonLoading(false);
      console.log(result as InternalServerError);
    }
  };

  const handleDeclineInvitation = async () => {
    setButtonLoading(true);
    const result = await UpdateUserStatus(user.id, { status: 'decline' });
    if ('code' in result) {
      // Handle the InternalServerError
      setError(result.message);
      setButtonLoading(false);
    } else {
      // Handle the successful response
      if (
        (result as InternalServerError).message ===
        'Successfully updated the user_status_id.'
      ) {
        setError((result as InternalServerError).message);
        setStep('declined');
      }
      setButtonLoading(false);
    }
  };

  const handleSaveMyPassword = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    setButtonLoading(true);
    const pwdError = await validatePassword(password);
    setPasswordError(pwdError);
    if (pwdError === null) {
      try {
        const { isSignUpComplete } = await signUp({
          username: user.email,
          password,
          options: {
            userAttributes: {
              email: user.email,
              given_name: user.first_name,
              family_name: user.last_name,
              'custom:user_id': String(user.id),
              'custom:tenant_id': String(user.tenant_id),
              'custom:user_type_id': String(user.user_type_id),
            },
          },
        });
        if (isSignUpComplete) {
          setStep('review');
          setButtonLoading(false);
        } else {
          setButtonLoading(false);
        }
      } catch (err) {
        console.log('handleSaveMyPassword error', err);
        const error = err as InternalServerError;
        if (
          error.message.includes(
            'PreSignUp failed with error PreSignUp failed: Email already exists in Cognito.',
          )
        ) {
          setError(
            'Email already exists in Cognito. Please Contact the Administrator',
          );
        } else {
          setError(error.message);
        }
        setButtonLoading(false);
      }
    } else {
      setButtonLoading(false);
    }
  };

  const handleReviewMyProfile = async () => {
    setButtonLoading(true);
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: user.email,
        password,
      });
      if (isSignedIn && nextStep.signInStep === 'DONE') {
        const { sub, email } = await fetchUserAttributes();
        if (sub && email) {
          // I am not sure, we have add a conditon to veriy user is approved/pending/reject. we are handling in login flow
          dispatch(
            setUser({
              email: email,
              id: sub,
              name:
                userRef.current.first_name + ' ' + userRef.current.last_name,
              userId: userRef.current.id,
              profileImageUrl: userRef.current.profile_image_url,
              userTypeId: userRef.current.user_type_id,
              schoolName: userRef.current.school_id,
              departmentName: userRef.current.department_id,
              isRememberSession: false,
            }),
          );
          getSupportsFromAPI();
          getUserTypesFromAPI();
          getDepartmentsFromAPI();
          getSchoolsFromAPI();
          setButtonLoading(false);
          getIPStatusesFromAPI();
          navigate(`/profile/${userRef.current.id}`);
        }
      }
    } catch (err: unknown) {
      console.error('ClaimProfile handleReviewMyProfile error: ', err);
      handleError(err);
    }
  };

  const handleError = async (err: unknown) => {
    console.error('Signin handleError error: ', err as InternalServerError);
    switch ((err as InternalServerError).message) {
      case 'Incorrect username or password.':
        setError('Your password is incorrect. Please reset your password or');
        break;
      case 'User does not exist.':
        setError(
          'An account with this email does not exist. Enter a different email or',
        );
        break;
      case 'There is already a signed in user.':
        await signOut();
        break;
      case 'A network error has occurred.':
        toast.error('You are offline. Please check your internet connection.');
        break;
      default:
        setError((err as InternalServerError).message || 'Error signing in');
    }
    setButtonLoading(false);
  };

  const renderStepContent = () => {
    switch (step) {
      case 'accept-invite':
        return (
          <div className="claim-profile-sub-container grid gap-24">
            {isTokenValid ? (
              <>
                <div className="grid gap-16">
                  <div className="text-large text-center">
                    Welcome to BUILT,{' '}
                    <span className="fw-sb">{user.first_name}</span>
                  </div>
                  <div className="grid gap-12">
                    <Text className="label-small">
                      You&apos;ve been invited to join BUILT, an exclusive
                      platform connecting the University of Virginia&apos;s
                      brightest minds with mentors, collaborators, and
                      groundbreaking opportunities.
                    </Text>
                    <Text className="label-small">
                      By accepting this invitation, you&apos;ll gain access to:
                    </Text>
                    <div className="label-small">
                      <ul className="mb-0 ps-4">
                        <li>
                          <span className="fw-b">Expert Guidance:</span> Connect
                          with experienced alumni and faculty ready to mentor
                          you.
                        </li>
                        <li>
                          <span className="fw-b">Collaborative Projects:</span>{' '}
                          Join forces with like-minded individuals to bring
                          innovative ideas to life.
                        </li>
                        <li>
                          <span className="fw-b">Exclusive Opportunities:</span>{' '}
                          Discover and participate in unique projects and
                          initiatives tailored to your interests.
                        </li>
                      </ul>
                    </div>
                    <Text className="label-small">
                      Don&apos;t miss out on the chance to be part of a vibrant
                      community driving the future of innovation and
                      entrepreneurship at UVA.
                    </Text>
                    <Text className="label-small">
                      Will you join us on this exciting journey?
                    </Text>
                  </div>
                </div>
                <div className="grid gap-12">
                  <Button
                    type="button"
                    className="primary-button"
                    onClick={handleAcceptInvitation}
                    disabled={buttonLoading != null && buttonLoading}
                  >
                    {buttonLoading ? (
                      <>
                        <Loading size={25} /> &nbsp; Accept Invitation
                      </>
                    ) : (
                      'Accept Invitation'
                    )}
                  </Button>
                  <Button
                    type="button"
                    className="secondary-button"
                    onClick={() => setStep('decline-appeal')}
                  >
                    Decline Invitation
                  </Button>
                </div>
                <div className="small-text">
                  By clicking &quot;Accept Invitation&quot; or using the BUILT
                  platform, you agree to these&nbsp;
                  <Link to="/terms-conditions" className="small-highlight-text">
                    Terms and Conditions
                  </Link>
                  &nbsp;and commit to protecting the ideas and intellectual
                  property of all users.
                </div>
              </>
            ) : (
              <>
                <Text className="large-semibold align-center">
                  The token is invalid or has expired
                </Text>
                <Button
                  type="button"
                  className="primary-button"
                  onClick={() => navigate('/')}
                >
                  Go to Home
                </Button>
              </>
            )}
          </div>
        );
      case 'decline-appeal':
        return (
          <div className="claim-profile-sub-container grid gap-24">
            <div className="grid gap-24">
              <div className="grid gap-16">
                <Text className="large-semibold">
                  Are You Sure You Want to Decline?
                </Text>
                <Text className="label-small">
                  We’d love for you to be part of the BUILT community and help
                  drive innovation at UVA. As a member, you can connect with
                  fellow alumni, support groundbreaking projects, and
                  collaborate with the University&apos;s brightest minds.
                </Text>
                <Text className="label-small">
                  Would you like to reconsider and explore the opportunities
                  BUILT offers?
                </Text>
              </div>
              <div className="grid gap-12">
                <Button
                  type="button"
                  className="primary-button"
                  onClick={() => setStep('accept-invite')}
                >
                  Join BUILT
                </Button>
                <Button
                  type="button"
                  className="secondary-button"
                  onClick={handleDeclineInvitation}
                  disabled={buttonLoading != null && buttonLoading}
                >
                  {buttonLoading ? (
                    <>
                      <Loading size={25} color="var(--black)" /> &nbsp; Yes,
                      Decline
                    </>
                  ) : (
                    'Yes, Decline'
                  )}
                </Button>
              </div>
            </div>
          </div>
        );
      case 'declined':
        return (
          <div className="claim-profile-sub-container grid gap-24">
            <div className="grid gap-24">
              <div className="grid gap-16">
                <Text className="label-small">
                  We understand and respect your decision.
                </Text>
                <Text className="label-small">
                  If you ever change your mind, you&apos;re welcome to come back
                  and request an invitation to join BUILT at any time. We&apos;d
                  love to have you in our community when the time is right for
                  you.
                </Text>
              </div>
              <div className="grid gap-12">
                <Button
                  type="button"
                  className="primary-button"
                  onClick={() => navigate('/')}
                >
                  Exit to Homepage
                </Button>
              </div>
            </div>
          </div>
        );
      case 'set-password':
        return (
          <div className="claim-profile-sub-container grid gap-24">
            <form
              onSubmit={handleSaveMyPassword}
              className="padding-0 grid gap-24"
            >
              <div className="grid gap-8">
                <Text className="heading-medium text-center">
                  Set your password
                </Text>
                <div className="grid gap-6">
                  <Label htmlFor="password" className="medium-text">
                    Password<sup className="fs-12">*</sup>
                  </Label>
                  <PasswordInput
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                      setPasswordError(null);
                    }}
                    placeholder="••••••••"
                    autoComplete="off"
                  />
                  <Label htmlFor="password" className="text-small-regular">
                    Min 8 characters and include 1 number, 1 special character,
                    and 1 uppercase and 1 lowercase.
                  </Label>
                  {error && (
                    <div className="error-message">
                      <p>{error}</p>
                    </div>
                  )}
                  {passwordError !== null && (
                    <div className="error-message">
                      Please provide the password as per above guidelines
                    </div>
                  )}
                </div>
              </div>
              <Button
                type="submit"
                className="primary-button"
                disabled={buttonLoading != null && buttonLoading}
              >
                {buttonLoading ? (
                  <>
                    <Loading size={25} /> &nbsp; Save My Password
                  </>
                ) : (
                  'Save My Password'
                )}
              </Button>
            </form>
          </div>
        );
      case 'review':
        return (
          <div className="claim-profile-sub-container grid gap-24">
            <div className="grid gap-16">
              <div className="text-center">
                <img
                  src={
                    user.profile_image_url !== null
                      ? user.profile_image_url
                      : NoPhoto
                  }
                  alt={user.first_name}
                  className="review-photo-logo"
                />
              </div>
              <div className="grid gap-8">
                <Text className="text-bold-center">
                  You are in, {user.first_name}!
                </Text>
                <div className="text-small-center grid gap-20">
                  <Text>
                    We&apos;re thrilled to have you join our community.{' '}
                  </Text>
                  <Text>
                    Next up, take a moment to check your prebuilt profile and
                    ensure the information is accurate.
                  </Text>
                </div>
              </div>
            </div>
            <Button
              type="button"
              className="primary-button"
              onClick={handleReviewMyProfile}
              disabled={buttonLoading != null && buttonLoading}
            >
              {buttonLoading ? (
                <>
                  <Loading size={25} /> &nbsp; Review My Profile
                </>
              ) : (
                'Review My Profile'
              )}
            </Button>
          </div>
        );
      case 'complete':
        return (
          <div className="claim-profile-sub-container grid gap-24">
            <Text>Registration Complete</Text>
          </div>
        );
      case 'admin-not-approved':
        return (
          <div className="claim-profile-sub-container grid gap-24">
            <Text className="large-semibold align-center">
              Your account is pending approval by an administrator. Please
              contact admin support for further assistance.
            </Text>
            <Button
              type="button"
              className="primary-button"
              onClick={() => navigate('/')}
            >
              Go to Home
            </Button>
          </div>
        );
      case 'account-registered-awaiting-login':
        return (
          <div className="claim-profile-sub-container grid gap-24">
            <Text className="large-semibold align-center">
              Your account has been created. Please log in to continue.
            </Text>
            <Button
              type="button"
              className="primary-button"
              onClick={() => navigate('/signin')}
            >
              Go to Signin
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="claim-profile-container common-container justify-content-center align-items-center">
      {loading ? <Loading /> : renderStepContent()}
    </div>
  );
};

export default ClaimProfileFlow;
